import { InternalLink } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { IonBadge, IonButton, IonCard, IonCardSubtitle, IonIcon } from "@ionic/react";
import AutoAwesomeMotionOutlinedIcon from "@mui/icons-material/AutoAwesomeMotionOutlined";
import { Box, Stack } from "@mui/material";
import { ShiftContext } from "@src/app/components/workWithFriends/interface";
import { WorkWithFriends } from "@src/app/components/workWithFriends/workWithFriends";
import { checkForUrgentShift } from "@src/app/openShifts/urgentShifts/utils";
import { useSession } from "@src/app/store/helperHooks";
import { RootPaths } from "@src/appV2/App/paths";
import { CbhFeatureFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import { logEvent } from "@src/appV2/lib/analytics";
import { useGetShiftSlots } from "@src/appV2/OpenShifts/api/useGetShiftSlots";
import { SHIFT_BLOCKS_PATH } from "@src/appV2/ShiftBlocks/paths";
import { USER_EVENTS } from "@src/constants";
import { Facility, Shift } from "@src/lib/interface";
import { useAppSelector } from "@store/index";
import { chatbubblesOutline } from "ionicons/icons";
import { FC } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useMissingDocuments } from "../../dayView/custom-hooks/useMissingDocuments";
import { FacilityDetails } from "../components/facilityDetails";
import { ShiftTitle } from "../components/shiftTitle";
import { PaymentDisplay } from "../components/types";

export const CBHShiftItem: FC<{
  shift: Shift;
  agentId?: string;
  onClickOnItemFacilityDetails?: (
    shift: Shift,
    facility: Facility,
    displayExtraTimePayCard?: boolean
  ) => void;
}> = ({ shift, onClickOnItemFacilityDetails, agentId }) => {
  const history = useHistory();
  const { cbh, state, msa, county, hcf } = useMissingDocuments(shift);
  const ldFlags = useCbhFlags();
  const { channels } = useAppSelector((store) => store.chatStore);
  const { userId } = useSession();
  const currentChannel = channels.find(
    (channel) => channel.url === `${shift.facility?.userId}_${userId}`
  );

  const { isSuccess, data: shiftSlots } = useGetShiftSlots(
    {
      workplaceSlots: [
        {
          workplaceUserId: shift.facility?.userId ?? "",
          range: { start: shift.start ?? "", end: shift.end ?? "" },
        },
      ],
    },
    {
      enabled: isDefined(shift.facility?.userId),
    }
  );

  const shiftSlot = isSuccess ? shiftSlots[0] : undefined;

  const onCardClick = () => {
    logEvent(USER_EVENTS.SHIFT_CARD_CLICKED, {
      shiftId: shift._id,
      agentId,
    });

    history.push(`/home/myShifts/${shift._id}`);
  };

  const onChatButtonClick = () => {
    history.push(`/home/account/chat/${shift.facility?.userId}`);
  };

  const unreadMessageCount =
    (currentChannel?.unreadMessageCount || 0) <= 0 ? false : currentChannel?.unreadMessageCount;
  const hasMissingDocs = !!(cbh || state || msa || county || hcf);

  const showChatButton = !!currentChannel?.url && ldFlags["chat"];

  let paymentType: PaymentDisplay;
  const isUrgentShiftsHourlyPayRateEnabled =
    ldFlags[CbhFeatureFlag.URGENT_SHIFTS_HOURLY_PAY_RATE] ?? true;

  if (checkForUrgentShift(shift) && !isUrgentShiftsHourlyPayRateEnabled) {
    paymentType = "total";
  } else {
    paymentType = "hourly";
  }

  return (
    <IonCard
      mode="ios"
      button={true}
      className="ion-padding no-text-transform new-flow shifts ongoing"
      data-testid={`my-shift-item-${shift._id}`}
      onClick={onCardClick}
      role="button"
      style={{ cursor: "pointer" }}
    >
      <ShiftTitle
        shift={shift}
        hasMissingDocs={hasMissingDocs}
        showMissingDocs={!shift.agent}
        showPaymentAs={paymentType}
      />
      <FacilityDetails
        shift={shift}
        onFacilityDetailsClick={onClickOnItemFacilityDetails}
        showCheckInInstructions={true}
      />
      <IonCardSubtitle style={{ fontSize: "14px" }} className="ion-text-center">
        {!shift.agent && "Booked shift, Waiting for Confirmation"}
      </IonCardSubtitle>
      {isDefined(shiftSlot) && (
        <WorkWithFriends shiftSlot={shiftSlot} shift={shift} context={ShiftContext.MY_SHIFTS} />
      )}

      <Stack spacing={1.5}>
        {isDefined(shift.agentId) && isDefined(shift.shiftBlockId) && (
          <InternalLink
            underline="always"
            to={generatePath(
              `${RootPaths.APP_V2_HOME}/${SHIFT_BLOCKS_PATH}/:shiftBlockId/:agentId`,
              {
                shiftBlockId: shift.shiftBlockId ?? "",
                agentId: shift.agentId,
              }
            )}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AutoAwesomeMotionOutlinedIcon
                fontSize="small"
                sx={{
                  transform: "scaleX(-1)",
                }}
              />
              <span>View all shifts in block</span>
            </Stack>
          </InternalLink>
        )}
        {showChatButton && (
          <Box>
            <IonButton
              onClick={(event) => {
                event.stopPropagation();

                logEvent(USER_EVENTS.SHIFT_CARD_CHAT_BUTTON_CLICKED, {
                  shiftId: shift._id,
                  agentId: agentId,
                });

                onChatButtonClick();
              }}
              fill="outline"
              style={{ height: 34, color: "#4F4F4F", padding: 0 }}
              color="dark"
              shape="round"
              strong={true}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14, height: "100%" }}>
                <IonIcon style={{ height: 20, width: 20 }} icon={chatbubblesOutline} mode="ios" />
                <span style={{ color: "#4F4F4F" }}>Facility Chat</span>
                <IonBadge color="danger">{unreadMessageCount}</IonBadge>
              </div>
            </IonButton>
          </Box>
        )}
      </Stack>
    </IonCard>
  );
};
